import { makeAutoObservable, runInAction } from "mobx";
import { RootStore } from "../../shared/stores/RootStore";
import {
  buildEmptyUpdateSimulationWay,
  CreateSimulationWay,
  SimulationWay,
  simulationWayToUpdate,
  UpdateSimulationWay,
} from "../models/SimulationWay";
import { SimulationWayApi } from "../services/SImulationWayApi";
import {
  CreateOrUpdateSimulationLane,
  SimulationLane,
} from "../models/SimulationLane";

export class SimulationStore {
  rootStore: RootStore;

  selectedSimulationWay?: SimulationWay;
  selectedSimulationLanes: SimulationLane[] = [];
  isEditing = false;
  simulatedHlaWayIds = new Set<string>();
  isSimulationOpen = false;
  simulationWayId?: string;

  form: UpdateSimulationWay = buildEmptyUpdateSimulationWay();
  defaultToFrontFacing = true;
  dirtyIds = new Set<string>();
  savingChanges = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  resetState() {
    runInAction(() => {
      this.savingChanges = false;
      this.dirtyIds = new Set();
      this.selectedSimulationWay = undefined;
      this.selectedSimulationLanes = [];
      this.form = buildEmptyUpdateSimulationWay();
    });
  }

  async createSimulated(body: CreateSimulationWay) {
    const { data } = await SimulationWayApi.createSimulated(body);

    runInAction(() => {
      this.selectedSimulationWay = data.simulationWay;
      this.selectedSimulationLanes = data.simulationLanes ?? [];
      this.form = simulationWayToUpdate(
        data.simulationWay,
        data.simulationLanes ?? [],
      );
      this.isSimulationOpen = true;
      this.isEditing = true;
    });

    return data;
  }

  async loadSimulatedHlaWayIds(surveyId: string) {
    const { data } = await SimulationWayApi.listHlaWayIdsBySurvey(surveyId);
    console.log(data);
    runInAction(() => {
      this.simulatedHlaWayIds = new Set(data);
    });
  }

  selectSimulationWayId(simulationWayId: string) {
    runInAction(() => {
      this.simulationWayId = simulationWayId;
    });
  }

  async getSimulaions(hlaWayId: string) {
    try {
      const { data } = await SimulationWayApi.get(hlaWayId);

      runInAction(() => {
        this.selectedSimulationWay = data?.simulationWay;
        this.selectedSimulationLanes = data?.simulationLanes ?? [];
        this.form = data?.simulationWay
          ? simulationWayToUpdate(
              data.simulationWay,
              data.simulationLanes ?? [],
            )
          : buildEmptyUpdateSimulationWay();

        if (data?.simulationWay?.verified) {
          this.selectedSimulationWay = data.simulationWay;
          this.isSimulationOpen = true;
          this.isEditing = false;
        }
      });

      return data;
    } catch (error: any) {
      runInAction(() => {
        this.selectedSimulationWay = undefined;
        this.selectedSimulationLanes = [];
        this.form = buildEmptyUpdateSimulationWay();
      });

      if (error?.response?.status === 500) return null;
      throw error;
    }
  }

  getSelectedLane(): SimulationLane | undefined {
    if (!this.selectedSimulationLanes.length) return undefined;

    const frontFacingLane = this.selectedSimulationLanes.find(
      (lane) => lane.frontFacing,
    );
    const backFacingLane = this.selectedSimulationLanes.find(
      (lane) => !lane.frontFacing,
    );

    if (!this.defaultToFrontFacing && backFacingLane) {
      return backFacingLane;
    }

    return frontFacingLane ?? backFacingLane;
  }

  getSelectedFormLane(): CreateOrUpdateSimulationLane | null {
    if (this.defaultToFrontFacing) {
      return this.form.frontFacingLane;
    }
    return this.form.backFacingLane;
  }

  selectSimulationLane = (frontFacing: boolean) => {
    runInAction(() => {
      this.defaultToFrontFacing = frontFacing;
    });
  };

  code = (callback: (prev: UpdateSimulationWay) => UpdateSimulationWay) => {
    runInAction(() => {
      this.form = callback(this.form);

      if (this.selectedSimulationWay?.id) {
        this.markAsDirty(this.selectedSimulationWay.id);
      }
    });
  };

  verify() {
    runInAction(() => {
      this.form = { ...this.form, verified: true };

      if (this.selectedSimulationWay) {
        this.selectedSimulationWay.verified = true;
      }
    });
  }

  async saveChanges({ finish = false } = {}) {
    if (!this.selectedSimulationWay?.id) return;

    runInAction(() => {
      this.savingChanges = true;
    });

    try {
      const { data } = await SimulationWayApi.update(
        this.selectedSimulationWay.id,
        this.form,
      );

      const updatedWay = data.simulationWay ?? data;
      const updatedLanes =
        data.simulationLanes ??
        [updatedWay.frontFacingLane, updatedWay.backFacingLane].filter(Boolean);

      runInAction(() => {
        this.selectedSimulationWay = updatedWay;
        this.selectedSimulationLanes = updatedLanes;
        this.form = simulationWayToUpdate(updatedWay, updatedLanes);

        this.isSimulationOpen = true;
        this.isEditing = !finish;

        this.dirtyIds.delete(updatedWay.id);
      });

      return { simulationWay: updatedWay, simulationLanes: updatedLanes };
    } finally {
      runInAction(() => {
        this.savingChanges = false;
      });
    }
  }

  private markAsDirty = (wayId: string) => {
    runInAction(() => {
      this.dirtyIds.add(wayId);
    });
  };

  openComparison() {
    runInAction(() => {
      this.isSimulationOpen = true;
      this.isEditing = false;
    });
  }
  editSimulation() {
    runInAction(() => {
      if (this.selectedSimulationWay) {
        this.form = simulationWayToUpdate(
          this.selectedSimulationWay,
          this.selectedSimulationLanes,
        );
      }
      this.isSimulationOpen = true;
      this.isEditing = true;
    });
  }
  finishSimulation() {
    runInAction(() => {
      this.isSimulationOpen = true;
      this.isEditing = false;
    });
  }

  backToHla() {
    runInAction(() => {
      this.form = buildEmptyUpdateSimulationWay();
      this.defaultToFrontFacing = true;
      this.isSimulationOpen = false;
      this.isEditing = false;
    });
  }

  clearSelectedSimulation() {
    runInAction(() => {
      this.selectedSimulationWay = undefined;
      this.selectedSimulationLanes = [];
      this.defaultToFrontFacing = true;
      this.form = buildEmptyUpdateSimulationWay();
      this.isEditing = false;
      this.isSimulationOpen = false;
      this.resetState();
    });
  }

  async delete(simulationWayId: string) {
    await SimulationWayApi.deleteSimulation(simulationWayId);
    this.clearSelectedSimulation();
  }
}
